import { useMemo, useRef, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import {
  createRumAnalyticsAdapter,
  type ChatAnalyticsEvent,
  type ChatAnalyticsOptions,
} from "@hopper-ai/chat-widget";
import { mapHtsAssistAnalyticsEvent } from "./htsAssistAnalyticsEvents";
import { usePublishRBCEvent } from "@/hooks/shared/usePublishRBCEvent";

// crypto.randomUUID is missing from older WebViews; the id only has to be
// unique enough to group one session's events.
const createWidgetSessionId = (): string =>
  typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * Chat-widget analytics options for one HTS Assist session: the RBC
 * GTM/native-bridge pipeline plus the portal's own Datadog RUM (ASSIST-933).
 * RUM buffers calls until it is initialized, which is never in development.
 *
 * Pass the same object to `ChatWidgetConfig.analytics` and to
 * `A2uiChatWidgetProvider`'s `analytics` prop (A2UI action events flow through
 * the provider, not the widget config). It is stable for a session, so both
 * share one `widget_session_id`, and a new `sessionKey` starts a new one.
 */
export const useHtsAssistAnalytics = ({
  sessionKey,
  entryPoint,
  chatProvider,
}: {
  sessionKey: number;
  /** Entry point slug sent on the token request, e.g. `rbc-home`. */
  entryPoint: string | undefined;
  chatProvider: { getConversationId(): string | null } | undefined;
}): ChatAnalyticsOptions => {
  const pushToDataLayer = usePublishRBCEvent();
  // The provider is created after the chat opens; read it per event.
  const chatProviderRef = useRef(chatProvider);
  chatProviderRef.current = chatProvider;
  const [mountId] = useState(createWidgetSessionId);
  const widgetSessionId = `${mountId}:${sessionKey}`;

  const rumAdapter = useMemo(
    () =>
      createRumAnalyticsAdapter(datadogRum, {
        portal: "b2bportal",
        tenant: "rbc",
        entryPoint,
        widgetSessionId,
        getConversationId: () => chatProviderRef.current?.getConversationId(),
      }),
    [entryPoint, widgetSessionId]
  );

  return useMemo(
    () => ({
      adapters: [
        {
          onEvent: (event: ChatAnalyticsEvent) => {
            const rbcEvent = mapHtsAssistAnalyticsEvent(event);
            if (rbcEvent) {
              pushToDataLayer(rbcEvent.eventName, rbcEvent.properties);
            }
          },
        },
        rumAdapter,
      ],
    }),
    [pushToDataLayer, rumAdapter]
  );
};
